import React, { useContext, useEffect, useState } from "react";
import { Modal, Box, Typography, Alert } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Button from "../components/Button";
import UserContext from "../assets/user_context";
import { useNavigate } from "react-router-dom";
import { OrgSelector } from "./components/OrgSelector";

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1.25),
  boxShadow: theme.shadows[24],
  width: "max-content",
  minWidth: "400px",
}));

const ModalInner = styled(Box)(({ theme}) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(5, 4, 5, 4),
  width: "100%",
}));

const SelectOrg = () => {
  const {
    setOrg,
    userData,
    selectOrgOpen,
    setSelectOrgOpen,
    setIsChildDataLoaded,
    org,
    markManualModalClose,
    controlSessions,
  } = useContext(UserContext);

  const theme = useTheme();

  // Check if currently controlling any org
  const isControlling = controlSessions && controlSessions.length > 0;

  // Check if we're in a browser environment
  const [isBrowser, setIsBrowser] = useState(false);

  useEffect(() => {
    setIsBrowser(
      typeof window !== "undefined" && typeof document !== "undefined",
    );
  }, []);

  // Initialize to current org if available, otherwise first org
  const [selectOrg, setSelectOrg] = useState(() => {
    if (!userData?.orgs) return null;
    if (org && userData.orgs.find((o) => o.orgID === org)) {
      return org;
    }
    return userData.orgs.length > 0 ? userData.orgs[0].orgID : null;
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (userData?.orgs?.length > 0) {
      // Set to current org if it exists in user's orgs, otherwise first org
      if (org && userData.orgs.find((o) => o.orgID === org)) {
        setSelectOrg(org);
      } else {
        setSelectOrg(userData.orgs[0].orgID);
      }
    }
  }, [userData?.orgs, org]);

  const setWorkingOrg = async () => {
    if (selectOrg === org) {
      //Selected the same org that is already loaded
      setIsChildDataLoaded(true);
      markManualModalClose();
      setSelectOrgOpen(false);
    } else {
      //Changed org to selectOrg
      navigate("/");
      setOrg(selectOrg);
      markManualModalClose();
      setSelectOrgOpen(false);
    }
  };

  const handleModalClose = () => {
    markManualModalClose();
    setSelectOrgOpen(false);
    setIsChildDataLoaded(true);
  };

  // Don't render Modal until we're in browser environment
  if (!isBrowser) {
    return null;
  }

  return (
    <React.Fragment>
      <Modal open={selectOrgOpen} onClose={handleModalClose}>
        <ModalBox>
          <ModalInner>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                mb: 3,
                color: theme.palette.text.primary,
                textAlign: "center",
              }}>
              Select an Organization
            </Typography>

            {isControlling && (
              <Alert severity="warning" sx={{ mb: 3, width: "100%" }}>
                You are currently controlling an organization. Please release
                control using the banner at the top before switching
                organizations.
              </Alert>
            )}

            {userData?.orgs?.length > 0 ? (
              <React.Fragment>
                <OrgSelector
                  userData={userData}
                  selectedOrg={selectOrg}
                  onOrgChange={setSelectOrg}
                  disabled={isControlling}
                />
                <Button
                  variant="contained"
                  onClick={setWorkingOrg}
                  disabled={isControlling}>
                  Select
                </Button>
              </React.Fragment>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  textAlign: "center",
                }}>
                No organizations available
              </Typography>
            )}
          </ModalInner>
        </ModalBox>
      </Modal>
    </React.Fragment>
  );
};

export default SelectOrg;
